import { useState } from "react";
import { ChevronDown, ChevronRight, Wrench } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiSWR } from "@/hooks/useApiSWR";
import { getMcpToolCatalog, MCP_URL, type McpTool } from "@/services/mcp-connection.service";

function ToolRow({ tool }: { tool: McpTool }) {
	const [open, setOpen] = useState(false);
	const params = Object.entries(tool.inputSchema.properties ?? {});
	const required = new Set(tool.inputSchema.required ?? []);

	return (
		<li className="border-b border-border last:border-b-0">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				aria-expanded={open}
				className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-muted-subtle"
			>
				{open ? (
					<ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
				) : (
					<ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
				)}
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<code className="text-xs font-semibold text-foreground">{tool.name}</code>
						<span className="rounded-md bg-muted-subtle px-1.5 py-0.5 text-[10px] text-muted-foreground">
							{tool.permission ?? "always available"}
						</span>
					</div>
					<p className="mt-0.5 text-xs text-muted-foreground">{tool.description}</p>
				</div>
			</button>
			{open && (
				<div className="px-9 pb-3">
					{params.length ? (
						<ul className="space-y-1">
							{params.map(([name, p]) => (
								<li key={name} className="text-xs">
									<code className="text-foreground">{name}</code>
									<span className="text-muted-foreground">
										{" "}
										{p.type}
										{required.has(name) ? ", required" : ""}
										{p.enum ? ` (${p.enum.join(" | ")})` : ""}
										{p.description ? ` - ${p.description}` : ""}
									</span>
								</li>
							))}
						</ul>
					) : (
						<p className="text-xs text-muted-foreground">No parameters.</p>
					)}
				</div>
			)}
		</li>
	);
}

export function McpToolCatalog() {
	const { data, error, isLoading } = useApiSWR("mcp-tools", getMcpToolCatalog);

	return (
		<div className="space-y-2">
			<div className="flex items-center gap-2">
				<Wrench className="h-4 w-4 text-muted-foreground" />
				<h4 className="text-sm font-semibold text-foreground">Available tools</h4>
			</div>
			<p className="text-xs text-muted-foreground">
				Agents discover these via MCP <code>tools/list</code> (filtered to the connection's
				permissions) or the public catalog at <code className="break-all">{MCP_URL}/tools</code>.
			</p>
			{isLoading ? (
				<Skeleton className="h-32 w-full" />
			) : error ? (
				<p className="text-xs text-danger">Failed to load tools.</p>
			) : (
				<ul className="rounded-lg border border-border">
					{data?.map((t) => <ToolRow key={t.name} tool={t} />)}
				</ul>
			)}
		</div>
	);
}
